import { useEffect } from "react";
import { toast } from "sonner";

/**
 * useTabSwitchDetection — monitors tab switches for exam quizzes.
 *
 * Tracks how many times the user leaves the page. After 3 switches,
 * it calls the onThresholdReached callback (e.g., to auto-submit the quiz).
 *
 * @param {object} options
 * @param {boolean} options.enabled - Whether detection is active (e.g., only for exam quizzes)
 * @param {string} options.quizId - The quiz ID used for localStorage key
 * @param {function} options.onThresholdReached - Callback invoked after 3rd tab switch
 * @param {number} options.threshold - Number of switches before triggering (default: 3)
 */
function useTabSwitchDetection({ enabled = false, quizId, onThresholdReached, threshold = 3 }) {
  useEffect(() => {
    if (!enabled || !quizId || !onThresholdReached) return;

    const STORAGE_KEY = `quiz_tab_switches_${quizId}`;
    let lastTriggerTime = 0;

    const registerInfraction = () => {
      const now = Date.now();
      // Prevent double counting within 500ms (e.g. blur + visibilitychange firing together)
      if (now - lastTriggerTime < 500) return;
      lastTriggerTime = now;

      const currentCount = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      const newCount = currentCount + 1;
      localStorage.setItem(STORAGE_KEY, newCount);

      const remaining = threshold - newCount;

      if (newCount >= threshold) {
        toast.error(
          `You left the exam page too many times! Submitting your exam...`,
          { duration: 5000 }
        );
        onThresholdReached();
      } else {
        toast.warning(`Warning ${newCount}/${threshold}: Don't leave the exam page!`, {
          description: `You have ${remaining} ${remaining === 1 ? "warning" : "warnings"} left before auto-submission.`,
          duration: 4000,
          action: {
            label: "Return",
            onClick: () => window.focus(),
          },
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        registerInfraction();
      }
    };

    const handleBlur = () => {
      registerInfraction();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      localStorage.removeItem(STORAGE_KEY);
    };
  }, [enabled, quizId, onThresholdReached, threshold]);
}

export default useTabSwitchDetection;
